/**
 * Survey Connect — Premium Fintech Design System
 * 
 * This is the master theme file. It exports:
 *   - Design tokens (colors, spacing, typography, radii, shadows)
 *   - CSS variable helper functions for dynamic theming
 *   - Re-exports from webStyles (component patterns) and ThemeProvider
 */
import { Platform } from 'react-native';

/* ─── Import CSS animations (web only — Metro can't handle .css) ── */
if (Platform.OS === 'web') {
  require('./animations.css');
}

/* ─── Helper: on native, strip var() wrappers and use the fallback ── */
function c(cssVar: string, fallback: string): string {
  return Platform.OS === 'web' ? cssVar : fallback;
}

/* ─── Color Tokens (static, for non-themed contexts) ─────────── */
export const colors = {
  // Brand
  primary: c('var(--sc-brand-primary, #0C5ADB)', '#0C5ADB'),
  primaryLight: c('var(--sc-brand-primary-light, #4E8DF5)', '#4E8DF5'),
  primaryDark: c('var(--sc-brand-primary-dark, #0840A0)', '#0840A0'),
  secondary: c('var(--sc-success, #229A39)', '#229A39'),
  secondaryLight: c('var(--sc-brand-primary-light, #54D06E)', '#54D06E'),

  // Backgrounds
  background: c('var(--sc-bg-primary, #F5F7FA)', '#F5F7FA'),
  surface: c('var(--sc-bg-secondary, #FFFFFF)', '#FFFFFF'),
  card: c('var(--sc-bg-card, #FFFFFF)', '#FFFFFF'),
  hero: c('var(--sc-bg-hero, #0A1C30)', '#0A1C30'),

  // Text
  text: c('var(--sc-text-primary, #0A1C30)', '#0A1C30'),
  textSecondary: c('var(--sc-text-secondary, #475569)', '#475569'),
  textLight: c('var(--sc-text-tertiary, #94A3B8)', '#94A3B8'),
  textOnPrimary: c('var(--sc-text-on-brand, #FFFFFF)', '#FFFFFF'),

  // Borders
  border: c('var(--sc-border, #E2E8F0)', '#E2E8F0'),
  divider: c('var(--sc-divider, #F1F5F9)', '#F1F5F9'),

  // Semantic
  success: c('var(--sc-success, #229A39)', '#229A39'),
  warning: c('var(--sc-warning, #F59E0B)', '#F59E0B'),
  error: c('var(--sc-error, #EF4444)', '#EF4444'),
  info: c('var(--sc-info, #3B82F6)', '#3B82F6'),

  // State colors
  stateSubmitted: c('var(--sc-text-tertiary, #64748B)', '#64748B'),
  stateReview: c('var(--sc-info, #3B82F6)', '#3B82F6'),
  stateApproved: c('var(--sc-success, #229A39)', '#229A39'),
  stateAssigned: '#8B5CF6', // Purple custom
  stateNegotiating: c('var(--sc-warning, #F59E0B)', '#F59E0B'),
  stateConfirmed: c('var(--sc-success, #229A39)', '#229A39'),
  stateClosed: c('var(--sc-text-tertiary, #64748B)', '#64748B'),
  stateDisputed: c('var(--sc-error, #EF4444)', '#EF4444'),

  // Chat
  chatBubbleSelf: c('var(--sc-bg-active, #E8F1FD)', '#E8F1FD'),
  chatBubbleOther: c('var(--sc-bg-secondary, #FFFFFF)', '#FFFFFF'),
  chatSystem: c('var(--sc-bg-hover, #F1F5F9)', '#F1F5F9'),
  chatBlocked: c('var(--sc-error-bg, #FEE2E2)', '#FEE2E2'),

  overlay: c('var(--sc-bg-overlay, rgba(0, 0, 0, 0.5))', 'rgba(0, 0, 0, 0.5)'),
  skeleton: c('var(--sc-bg-skeleton, #E2E8F0)', '#E2E8F0'),

  // Sidebar
  sidebarBg: c('var(--sc-bg-secondary, #FFFFFF)', '#FFFFFF'),
  sidebarActive: c('var(--sc-brand-primary, #0C5ADB)', '#0C5ADB'),
  sidebarText: c('var(--sc-text-secondary, #636E72)', '#636E72'),
  sidebarHover: c('var(--sc-bg-hover, #F5F6FA)', '#F5F6FA'),

  // Card accent colors
  cardOrange: '#FF6B35',
  cardPurple: '#7C3AED',
  cardTeal: '#0C5ADB',
  cardPink: '#E91E8A',
  cardGreen: '#229A39',
  cardBlue: '#3B82F6',
};

/* ─── Spacing Scale (px values for CSS-in-JS) ────────────────── */
export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
};

/* ─── Font Sizes ─────────────────────────────────────────────── */
export const fontSize = {
  caption: 12,
  bodyRegular: 14,
  bodyLarge: 16,
  h3: 18,
  h2: 20,
  h1: 24,
  
  // Legacy mappings to prevent instant crashes during refactoring
  xs: 12,
  sm: 12,
  md: 14,
  base: 16,
  lg: 18,
  xl: 20,
  xxl: 24,
  xxxl: 24,
  display: 24,
};

/* ─── Border Radii ───────────────────────────────────────────── */
export const borderRadius = {
  sm: 8,
  md: 12,
  lg: 16,
  round: 9999,
  
  // Legacy mappings to prevent instant crashes during refactoring
  xs: 8,
  xl: 16,
  xxl: 16,
};

/* ─── Shadows (CSS-in-JS) ───────────────────────────────────── */
export const shadows = {
  xs: {
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.02)',
  },
  sm: {
    boxShadow: '0 2px 8px rgba(15, 23, 42, 0.02), 0 1px 2px rgba(15, 23, 42, 0.01)',
  },
  md: {
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03), 0 1px 2px rgba(15, 23, 42, 0.02)',
  },
  lg: {
    boxShadow: '0 10px 25px rgba(15, 23, 42, 0.05), 0 2px 8px rgba(15, 23, 42, 0.03)',
  },
  xl: {
    boxShadow: '0 20px 40px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(15, 23, 42, 0.04)',
  },
  brand: {
    boxShadow: '0 4px 14px rgba(12, 90, 219, 0.15), 0 1px 3px rgba(0, 0, 0, 0.05)',
  },
  card: {
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.03), 0 1px 2px rgba(15, 23, 42, 0.02)',
  },
};

/* ─── Touch Targets ──────────────────────────────────────────── */
export const touch = {
  min: 44,
  button: 48,
  input: 48,
};

/* ─── Transitions ────────────────────────────────────────────── */
export const transitions = {
  fast: '0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  base: '0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  slow: '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  spring: '0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
};

/* ─── CSS Variable Helpers ───────────────────────────────────── */
/** Read a CSS variable value. Falls back to the provided default. */
export function cssVar(name: string, fallback?: string): string {
  return `var(${name}${fallback ? `, ${fallback}` : ''})`;
}

/* ─── Re-exports (web-only modules guarded by Platform) ──────── */
// webStyles.ts uses React.CSSProperties, CSS vars, and web-only patterns.
// ThemeProvider.tsx uses document, localStorage, window.matchMedia.
// These crash Metro, so we only re-export them on web.

export type ThemeMode = 'light' | 'dark';

if (Platform.OS === 'web') {
  // Re-exports happen via the wildcard below
}

// Always export webStyles on web (tree-shaking handles native)
export * from './webStyles';

// ThemeProvider: export real on web, no-op on native
export { ThemeProvider, useTheme } from './ThemeProvider';

